import { Document } from '@/app/pages/documentlibrary/mockDocuments';

type DocumentStatus = 'Draft' | 'Current' | 'Expired' | 'Deleted';

/**
 * Determines the appropriate status for a document based on its next review date
 * @param document The document to check
 * @returns The determined status (Draft, Current, or Expired)
 */
export const determineDocumentStatus = (document: Partial<Document>): DocumentStatus => {
  // If status is explicitly set to Deleted, keep it as is
  if (document.status === 'Deleted') {
    return 'Deleted';
  }

  // If no nextReviewDate, default to Draft
  if (!document.nextReviewDate) {
    return 'Draft';
  }

  let nextReviewDate: Date | null = null;

  // Handle different nextReviewDate formats
  if (document.nextReviewDate instanceof Date) {
    nextReviewDate = document.nextReviewDate;
  } else if (typeof document.nextReviewDate === 'string') {
    nextReviewDate = new Date(document.nextReviewDate);
  } else if (document.nextReviewDate && typeof document.nextReviewDate === 'object') {
    // Handle Firestore timestamp or similar objects
    const dateObj = document.nextReviewDate as any;
    if (dateObj.toDate && typeof dateObj.toDate === 'function') {
      nextReviewDate = dateObj.toDate();
    } else if (dateObj.seconds) {
      // Handle Firestore timestamp in seconds
      nextReviewDate = new Date(dateObj.seconds * 1000);
    } else if (dateObj.__time__) {
      // Handle custom timestamp format
      nextReviewDate = new Date(dateObj.__time__);
    }
  }

  // If we couldn't parse the date, default to Draft
  if (!nextReviewDate || isNaN(nextReviewDate.getTime())) {
    return 'Draft';
  }

  // Compare with current date
  const now = new Date();
  
  // If nextReviewDate is in the past, document is Expired
  if (nextReviewDate < now) {
    return 'Expired';
  }

  // If we have a status and it's not Draft, keep it (to preserve manual overrides)
  if (document.status && document.status !== 'Draft') {
    return document.status;
  }

  // Default to Current for valid future dates
  return 'Current';
};

/**
 * Ensures a document has a valid status based on its nextReviewDate
 * @param document The document to update
 * @returns A new document with the status updated if needed
 */
export const withAutoStatus = <T extends Partial<Document>>(document: T): T & { status: DocumentStatus } => {
  // If document already has a status and it's not Draft, don't change it
  if (document.status && document.status !== 'Draft') {
    return { ...document, status: document.status as DocumentStatus };
  }

  const status = determineDocumentStatus(document);
  return { ...document, status };
};
